import React, { useState } from 'react';
import Modal from './Modal';
import UnteraufgabenEditor from './UnteraufgabenEditor';
import { PRIORITAETEN } from './todoConstants';

/** Erzeugt den Anfangszustand des Formulars (leer oder aus einer bestehenden Aufgabe). */
const initialFormular = (todo) => ({
  titel: todo?.titel ?? '',
  beschreibung: todo?.beschreibung ?? '',
  prioritaet: todo?.prioritaet ?? 'MITTEL',
  faelligkeitsdatum: todo?.faelligkeitsdatum ?? '',
  kategorieId: todo?.kategorieId != null ? String(todo.kategorieId) : '',
  unteraufgaben: todo?.unteraufgaben?.map((u) => ({ titel: u.titel, erledigt: u.erledigt })) ?? [],
});

/**
 * Modal-Formular zum Anlegen/Bearbeiten einer Aufgabe.
 *
 * @param {{ todo?: object, kategorien: object[], onSubmit: (daten) => void, onClose: () => void }} props
 */
export default function TodoForm({ todo, kategorien, onSubmit, onClose }) {
  const [formular, setFormular] = useState(() => initialFormular(todo));

  const setFeld = (feld, wert) => setFormular((f) => ({ ...f, [feld]: wert }));

  const speichern = (e) => {
    e.preventDefault();
    if (!formular.titel.trim()) return;
    onSubmit({
      ...todo,
      titel: formular.titel.trim(),
      beschreibung: formular.beschreibung.trim() || null,
      prioritaet: formular.prioritaet,
      faelligkeitsdatum: formular.faelligkeitsdatum || null,
      kategorieId: formular.kategorieId ? Number(formular.kategorieId) : null,
      erledigt: todo?.erledigt ?? false,
      unteraufgaben: formular.unteraufgaben.filter((u) => u.titel.trim()),
    });
  };

  return (
    <Modal titel={todo ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'} onClose={onClose}>
      <form onSubmit={speichern} className="space-y-4">
        <Feld label="Titel">
          <input
            type="text"
            value={formular.titel}
            onChange={(e) => setFeld('titel', e.target.value)}
            autoFocus
            className="w-full rounded-lg border border-line bg-paper-soft px-3 py-2 text-ink outline-none focus:border-brand-500"
          />
        </Feld>

        <Feld label="Beschreibung">
          <textarea
            value={formular.beschreibung}
            onChange={(e) => setFeld('beschreibung', e.target.value)}
            rows={2}
            className="w-full resize-y rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
          />
        </Feld>

        <div className="grid grid-cols-2 gap-4">
          <Feld label="Priorität">
            <select
              value={formular.prioritaet}
              onChange={(e) => setFeld('prioritaet', e.target.value)}
              className="w-full rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
            >
              {PRIORITAETEN.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </Feld>

          <Feld label="Fällig am">
            <input
              type="date"
              value={formular.faelligkeitsdatum}
              onChange={(e) => setFeld('faelligkeitsdatum', e.target.value)}
              className="w-full rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
            />
          </Feld>
        </div>

        <Feld label="Kategorie">
          <select
            value={formular.kategorieId}
            onChange={(e) => setFeld('kategorieId', e.target.value)}
            className="w-full rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
          >
            <option value="">Keine</option>
            {kategorien.map((k) => (
              <option key={k.uuid} value={k.id}>{k.name}</option>
            ))}
          </select>
        </Feld>

        <Feld label="Unteraufgaben">
          <UnteraufgabenEditor
            items={formular.unteraufgaben}
            onChange={(items) => setFeld('unteraufgaben', items)}
          />
        </Feld>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5">
            Abbrechen
          </button>
          <button type="submit" className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500">
            Speichern
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Beschriftetes Formularfeld. */
function Feld({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-xs uppercase tracking-wide text-ink-muted">{label}</span>
      {children}
    </label>
  );
}
